import React, { FormEvent, useState } from 'react'
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  CircularProgress,
  Button,
  useTheme,
} from '@mui/material'
import { Months } from '../lib/Months';
import cca2List from '../lib/Countries/cca2.json';
import countries from '../lib/Countries/countries.json';
import moment from 'moment';
import { usernameAvailability } from '../utils/lyfshortService';
import * as yup from 'yup'
import { phoneUtil } from '../utils/phoneUtil';
import { GenderType } from '../types/authState';


type Props = {
  onSingup: (email: string, passowrd: string, name: string, extra: object) => void;
  inProgress: boolean;
}

const items = cca2List
  .map((cca2) => ({ ...(countries as Record<string, any>)[cca2], cca2 }))
  .map((c) => ({
    label: c.name.common,
    value: c.cca2,
  }));

interface FormErrors {
  [key: string]: string;
}

const schema = yup.object().shape({
  firstName: yup.string().required(),
  lastName: yup.string().required(),
  password: yup.string().required(),
  email: yup.string().email().required(),
  username: yup
    .string()
    .min(1)
    .max(50)
    .matches(
      /^[a-zA-Z0-9\-_]+$/,
      'must only contain letters A-Z, numbers 0-9, underscore _, or dash -'
    )
    .test('is-available', 'This username is already in use', (value: string | undefined) => {
      if (value) {
        return usernameAvailability(value)
          .then((result) => result.available)
          .catch(() => {
            throw new Error('Error finding out if username is available')
          });
      }
    })
    .required(),
  homeLocationCountry: yup.string().required(),
  mobilePhone: yup
    .string()
    .test('is-valid', 'Not a valid phone number', (value) => {
      if (value == '' || !value) {
        return true;
      }

      try {
        phoneUtil.parseAndKeepRawInput(value);
        return true;
      } catch (e) {
        return false;
      }
    }),
  sex: yup.string(),
  birthDate: yup
    .date()
    .max(moment().subtract(13, 'years').startOf('day').toDate())
    .required(),
});

const ProfileSignup = (props: Props) => {
  const { onSingup, inProgress } = props
  const theme = useTheme();

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fName, setFName] = useState('')
  const [lName, setLName] = useState('')
  const [username, setUsername] = useState('')
  const [homeLocationCountry, setHomeLocationCountry] = useState('')
  const [ageMonth, setAgeMonth] = useState<string>('')
  const [ageYear, setAgeYear] = useState<string>('')
  const [gender, setGender] = useState<GenderType>('')
  const [errors, setErrors] = useState<FormErrors>({})

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    try {
      let birthDate = moment().date(1)

      if (ageMonth) birthDate.month(ageMonth)
      if (ageYear) birthDate.year(parseInt(ageYear))

      const extraData = {
        firstName: fName,
        lastName: lName,
        username: username,
        homeLocationCountry,
        sex: gender,
        birthDate: birthDate.startOf('day').format('YYYY-MM-DD'),
      }

      const data = {
        email: email,
        password: password,
        ...extraData
      }

      await schema.validate(data, { abortEarly: false })      

      onSingup(email, password, `${fName} ${lName}`, extraData)
    } catch (error) {
      const errors: FormErrors = {};
      if (error instanceof yup.ValidationError) {        
        error.inner.forEach((err) => {
          const path = err.path || 'global';
          if (!errors[path]) {
            errors[path] =
              path === 'birthDate'
                ? 'You must be 13 years or older'
                : err.message.charAt(0).toUpperCase() + err.message.slice(1);
          }
        });
      } else {
        errors.global = (error as Error).message;
      }
      setErrors(errors);
    }
  }


  return (
    <>
      <Typography
        variant="h4"
        align="center"
        gutterBottom
        sx={{
          color: theme.palette.grey[800],
          fontWeight: 500,
          mb: 3
        }}
      >
        Create Account
      </Typography>
      <form onSubmit={handleSubmit}>
        <TextField
          fullWidth
          label="Email"
          variant="outlined"
          margin="normal"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          type="email"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="First Name"
          variant="outlined"
          margin="normal"
          value={fName}
          onChange={(e) => setFName(e.target.value)}
          required
          type="text"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Last Name"
          variant="outlined"
          margin="normal"
          value={lName}
          onChange={(e) => setLName(e.target.value)}
          required
          type="text"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Username"
          variant="outlined"
          margin="normal"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          required
          type="text"
          error={!!errors.username}
          helperText={errors.username}
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <TextField
          fullWidth
          label="Password"
          variant="outlined"
          margin="normal"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          type="password"
          sx={{
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: theme.palette.primary.main,
              },
            },
          }}
        />
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel id="country">Country</InputLabel>
          <Select
            labelId='country'
            id="country"
            label="Country"
            value={homeLocationCountry}
            required
            onChange={(e) => setHomeLocationCountry(e.target.value)}
          >
            <MenuItem disabled selected value="">Country *</MenuItem>
            {items.map(item => (
              <MenuItem value={item.value} key={item.value}>{item.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box display="flex" alignItems="center" mt={2} gap={1}>
          <FormControl fullWidth>
            <InputLabel id="monthOfBirth">Month of Birth</InputLabel>
            <Select
              labelId='month-of-birth'
              id="month-of-birth"
              label="Month of Birth"
              value={ageMonth}
              required
              onChange={(e) => setAgeMonth(e.target.value)}
            >
              <MenuItem disabled selected value="">Month of Birth*</MenuItem>
              {Months.map((month) => (
                <MenuItem value={month.toString()} key={month}>{month.toString()}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            label="Year of Birth"
            variant="outlined"
            margin="normal"
            value={ageYear}
            onChange={(e) => setAgeYear(e.target.value)}
            required
            type="number"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              },
              margin: 0,
            }}
          />
        </Box>
        <FormControl fullWidth sx={{ marginTop: 2 }}>
          <InputLabel id="gender">Gender</InputLabel>
          <Select
            labelId='gender'
            id="gender"
            label="Gender"
            value={gender}
            required
            onChange={(e) => setGender(e.target.value as GenderType)}
          >
            <MenuItem disabled selected value="">Gender</MenuItem>
            <MenuItem value='f'>Female</MenuItem>
            <MenuItem value='m'>Male</MenuItem>
            <MenuItem value='o'>Non-binary</MenuItem>
          </Select>
        </FormControl>
        <Button
          fullWidth
          variant="contained"
          color="primary"
          size="large"
          type="submit"
          disabled={inProgress}
          sx={{
            mt: 3,
            mb: 2,
            height: 48,
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            },
          }}
        >
          {inProgress ? (
            <CircularProgress size={24} />
          ) : (
            'Create Account'
          )}
        </Button>
      </form>
    </>
  )
}

export default ProfileSignup